import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { getThemeColor } from '../utils/themeUtils';

const ThemeProvider = ({ children }) => {
  const appSettings = useSelector((state) => state.appSettings);
  const { appTheme, appDarkMode, appGradientEnabled, appHeaderInverse } = appSettings;

  useEffect(() => {
    // Get the theme color
    const themeColor = getThemeColor(appTheme);
    
    // Update CSS custom properties
    const root = document.documentElement;
    
    // Set theme color
    root.style.setProperty('--bs-app-theme', themeColor);
    
    // Calculate theme color RGB values
    const hexToRgb = (hex) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      } : null;
    };
    
    const rgb = hexToRgb(themeColor);
    if (rgb) {
      root.style.setProperty('--bs-app-theme-rgb', `${rgb.r}, ${rgb.g}, ${rgb.b}`);
    }
    
    // Set theme color for text (contrast color)
    const getContrastColor = (hex) => {
      const rgb = hexToRgb(hex);
      if (!rgb) return '#ffffff';
      
      // Calculate luminance
      const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
      return luminance > 0.5 ? '#000000' : '#ffffff';
    };
    
    const themeColorText = getContrastColor(themeColor);
    root.style.setProperty('--bs-app-theme-color', themeColorText);
    
    // Calculate hover colors (darker shade)
    const getHoverColor = (hex) => {
      const rgb = hexToRgb(hex);
      if (!rgb) return hex;
      
      return `rgb(${Math.max(0, rgb.r - 25)}, ${Math.max(0, rgb.g - 25)}, ${Math.max(0, rgb.b - 25)})`;
    };
    
    const hoverColor = getHoverColor(themeColor);
    root.style.setProperty('--bs-app-theme-hover', hoverColor);
    root.style.setProperty('--bs-app-theme-hover-color', getContrastColor(hoverColor));
    
    // Set gradient if enabled
    if (appGradientEnabled) {
      const gradient = `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`;
      root.style.setProperty('--bs-gradient', gradient);
    } else {
      root.style.setProperty('--bs-gradient', 'none');
    }
    
    // Update body classes for dark mode
    if (appDarkMode) {
      document.body.classList.add('dark-mode');
      document.documentElement.setAttribute('data-bs-theme', 'dark');
    } else {
      document.body.classList.remove('dark-mode');
      document.documentElement.setAttribute('data-bs-theme', 'light');
    }
    
    // Update header inverse styling
    const header = document.getElementById('header');
    if (header) {
      if (appHeaderInverse) {
        header.setAttribute('data-bs-theme', 'dark');
        header.classList.add('header-inverse');
      } else {
        header.setAttribute('data-bs-theme', 'light');
        header.classList.remove('header-inverse');
      }
    }
    
  }, [appTheme, appDarkMode, appGradientEnabled, appHeaderInverse]);

  return <>{children}</>;
};

export default ThemeProvider;
